/**
 * Authentication Context
 * Sole handler of token storage and auth state.
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import authService from '@services/authService';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Restore session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          const response = await authService.getProfile();
          if (response.success) {
            setUser(response.data?.user || response.data);
            setIsAuthenticated(true);
          }
        }
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    // authService.login returns { success, data: { user, accessToken, refreshToken } }
    const response = await authService.login({ email, password });
    if (response.success) {
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response;
  }, []);

  const register = useCallback(async (userData) => {
    const response = await authService.register(userData);
    if (response.success) {
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      setUser(response.data.user);
      setIsAuthenticated(true);
    }
    return response;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    return Array.isArray(roles) ? roles.includes(user.role) : user.role === roles;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated, login, register, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
